<script setup lang="ts">
import { useRouter } from 'vue-router'
import { useDrawerStore } from '@/stores/drawer'
import { useScenarioStore } from '@/stores/scenario'
import { storeToRefs } from 'pinia'
import type { ScenarioFormTab, ScenarioFormTabs } from '@/components/scenario-form/type/type'
import { computed, onMounted, ref, type PropType } from 'vue'
import { useAuthorizationStore } from '@/stores/authorization'
import IdentityManager from '@arcgis/core/identity/IdentityManager'
import OAuthInfo from '@arcgis/core/identity/OAuthInfo'
import { ARCGIS_PORTAL_URL } from '@/components/project-detail/constants/constants'
import { useArcgisLogin } from '@/components/project-detail/composable/arcgisLoginComposable'

const props = defineProps({
  tab: { type: String as PropType<ScenarioFormTab>, required: true },
  tabs: { type: Array as PropType<ScenarioFormTabs>, required: true },
  hideRunSimulation: { type: Boolean, default: false }
})

const emit = defineEmits(['updateTabValue'])

const router = useRouter()
const drawerStore = useDrawerStore()
const scenarioStore = useScenarioStore()
const { drawer } = storeToRefs(drawerStore)
const { currentItem: currentScenarioItem } = storeToRefs(scenarioStore)
const authorizationStore = useAuthorizationStore()
const { arcgisProxyPortalAppId } = useArcgisLogin()

const tab = computed({
  get: () => props.tab,
  set: (value: ScenarioFormTab) => emit('updateTabValue', value)
})

const statusChoices = ref<{ value: string; display_name: string }[]>([])

onMounted(async () => {
  drawer.value.shown = true
  const options = await scenarioStore.getScenarioOptions()
  statusChoices.value = options?.actions?.POST?.status?.choices || []
})

const draftStatusValue = computed(() => {
  return statusChoices.value.find((choice) => choice.display_name === 'Draft')?.value || 'Draft'
})

const previousView = computed(() => {
  const status = currentScenarioItem.value?.status || ''
  return status === draftStatusValue.value ? 'project' : 'calculation'
})

const disableRunCalculationByToken = computed(() => !authorizationStore.arcgisToken)

const disableRunCalculationButton = computed(() => {
  return (
    props.tabs.some((tab) => !tab.isCompleted) ||
    isReadOnly.value ||
    disableRunCalculationByToken.value
  )
})

const activeTab = (value: ScenarioFormTab) => {
  return tab.value === value ? 'active-tab' : ''
}

const completeTab = (value: boolean) => {
  return value || isReadOnly.value ? 'font-weight-bold' : 'font-weight-light'
}

const navigateToPreviousView = () => {
  if (previousView.value === 'project') {
    router.push({ name: 'project-detail', params: { id: currentScenarioItem.value?.project } })
  } else {
    router.push({
      name: 'scenario-result',
      params: {
        id: currentScenarioItem.value?.project,
        scenarioId: currentScenarioItem.value?.id
      }
    })
  }
}
const loading = ref(false)

const celeryCalculate = async () => {
  try {
    loading.value = true

    const oAuthInfo = new OAuthInfo({
      appId: arcgisProxyPortalAppId,
      portalUrl: ARCGIS_PORTAL_URL,
      popup: false
    })
    IdentityManager.registerOAuthInfos([oAuthInfo])

    const credentials = await IdentityManager.checkSignInStatus(`${ARCGIS_PORTAL_URL}/sharing`)
    authorizationStore.setToken(credentials.token)

    if (!currentScenarioItem.value) {
      throw new Error('No scenario selected.')
    }

    const response = await scenarioStore.celeryCalculate(
      currentScenarioItem.value,
      authorizationStore.arcgisToken
    )

    if (response) {
      await router.push({
        name: 'scenario-result',
        params: {
          id: currentScenarioItem.value.project,
          scenarioId: currentScenarioItem.value.id
        }
      })
    }
  } catch (error) {
    console.error('Error during calculation:', error)
  } finally {
    loading.value = false
  }
}

const isReadOnly = computed(() => {
  return currentScenarioItem.value?.status !== draftStatusValue.value
})
</script>

<template>
  <v-navigation-drawer v-model="drawer.shown">
    <v-list class="py-2">
      <v-list-item-action class="d-flex justify-center py-2">
        <v-btn
          class="text-none my-1 mx-2"
          variant="tonal"
          prepend-icon="mdi-arrow-left"
          @click.stop="navigateToPreviousView"
        >
          Back to {{ previousView }}
        </v-btn>
      </v-list-item-action>

      <v-list-item class="px-0" color="primary">
        <v-divider />
        <v-tabs
          v-model="tab"
          bg-color="transparent"
          color="primary"
          direction="vertical"
          style="z-index: 1"
        >
          <template v-for="(item, index) in tabs" :key="item.value">
            <v-tab
              v-if="item.isShown"
              :value="item.value"
              :class="['text-none', activeTab(item.value)]"
              rounded="0"
            >
              <span :class="['px-4', activeTab(item.value), completeTab(item.isCompleted)]">
                {{ item.label }}
              </span>
              <v-icon v-if="item.isCompleted || isReadOnly" color="primary">mdi-check</v-icon>
            </v-tab>
            <v-divider v-if="index < tabs.length - 1"></v-divider>
          </template>
        </v-tabs>
        <v-divider />
      </v-list-item>

      <v-list-item-action class="d-flex justify-center py-2" v-if="!props.hideRunSimulation">
        <v-tooltip v-if="disableRunCalculationByToken" location="top">
          <template #activator="{ props }">
            <span v-bind="props">
              <v-btn
                class="text-none my-1 mx-2"
                :variant="disableRunCalculationButton ? 'tonal' : 'flat'"
                :color="disableRunCalculationButton ? 'revert' : 'primary'"
                :disabled="disableRunCalculationButton"
                @click.stop="celeryCalculate"
              >
                Run simulation
              </v-btn>
            </span>
          </template>
          Please log in to Arcgis Online to run simulation
        </v-tooltip>
        <template v-else>
          <v-btn
            class="text-none my-1 mx-2"
            :variant="disableRunCalculationButton ? 'tonal' : 'flat'"
            :color="disableRunCalculationButton ? 'revert' : 'primary'"
            :disabled="disableRunCalculationButton"
            @click.stop="celeryCalculate"
          >
            Run simulation
          </v-btn>
        </template>
      </v-list-item-action>
    </v-list>
  </v-navigation-drawer>
  <v-dialog v-model="loading" persistent width="300">
    <v-card class="d-flex flex-column align-center pa-6">
      <v-progress-circular indeterminate color="primary" size="42" />
      <div class="mt-4">Checking Simulation Input</div>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.v-tab :deep(.v-tab__slider) {
  display: none;
}

.v-tab :deep(.v-btn__overlay) {
  display: none;
}

.active-tab {
  background-color: #ccecee;
  color: #303030;
}
</style>
